import { type FC } from 'react';
import { Form } from 'react-bootstrap';
import cx from 'clsx';

import { type VoiceRecording } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { voiceRecordingToVoiceRecordingProperties } from '@editor2/mappers/element';
import { voiceRecordingElement } from './styles';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: VoiceRecording;
	courseId: number;
}

const VoiceRecordingElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const debouncedDispatch = useDebouncedDispatch();

	const [voiceRecordingProperties, handleLocalChange] = useDebounceProtectedState(
		element,
		voiceRecordingToVoiceRecordingProperties
	);

	const handleChange = (value) => {
		handleLocalChange(value);
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleInstructionsChange = (value: string) => handleChange({ instructions: value });

	return (
		<Form className={cx(voiceRecordingElement, elementCommonStyles)}>
			<CopyInput label="Family ID" value={element.family_id} />

			<Form.Group>
				<Form.Label>Phone number for student to call</Form.Label>
				<Form.Control
					value={voiceRecordingProperties.phone}
					onChange={(e) => handleChange({ phone: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>
					{`Instructions - can contain HTML and {phone} and {pin} placeholders`}
				</Form.Label>
				<CodeMirrorHTML
					value={voiceRecordingProperties.instructions}
					onChange={handleInstructionsChange}
				/>
			</Form.Group>

			<Form.Group className="block">
				<Form.Check
					type="checkbox"
					checked={voiceRecordingProperties.autosubmit}
					onChange={(e) => handleChange({ autosubmit: !voiceRecordingProperties.autosubmit })}
					label="Autosubmit to LMS"
				/>
			</Form.Group>

			<Form.Group className="block">
				<Form.Label>LMS Column Name (for Autosubmit)</Form.Label>
				<Form.Control
					value={voiceRecordingProperties.column_name}
					onChange={(e) => handleChange({ column_name: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>{`Filename (for Autosubmit) - can contain {lastname}, {firstname}, and {iso8601date} placeholders`}</Form.Label>
				<Form.Control
					value={voiceRecordingProperties.filename_template}
					onChange={(e) => handleChange({ filename_template: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>LMS display name (for Autosubmit) e.g. Blackboard</Form.Label>
				<Form.Control
					value={voiceRecordingProperties.lms_name}
					onChange={(e) => handleChange({ lms_name: e.target.value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>LMS Submission Label</Form.Label>
				<Form.Control
					value={voiceRecordingProperties.autosubmit_button_label}
					onChange={(e) => handleChange({ autosubmit_button_label: e.target.value })}
				/>
			</Form.Group>
		</Form>
	);
};

export default VoiceRecordingElement;
